<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { Head, Link, router, useForm } from '@inertiajs/vue3'
import axios from 'axios'
import { debounce } from 'lodash'
import { notify } from '@kyvg/vue3-notification'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSearch, faBoxOpen, faChevronDown, faChevronRight, faInfoCircle } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import CatalogOptionRow from '@/Pages/Kasir/Brands/CatalogOptionRow.vue'
library.add(faSearch, faBoxOpen, faChevronDown, faChevronRight, faInfoCircle)

interface PriceTier {
    min_quantity: number
    price: number
}

interface CatalogVariation {
    id: number
    name: string
    detail?: string
    price_tiers: PriceTier[]
}

interface CatalogProduct {
    id: number
    sku: string
    name: string
    category?: string
    print_type_label?: string
    is_available: boolean
    price_tiers: PriceTier[]
    variations: CatalogVariation[]
}

interface PickedProduct {
    cs_product_id: number
    cs_product_variation_id: number | null
    label: string
    catalog_tiers: PriceTier[]
    tiers: PriceTier[]
}

const props = defineProps<{
    title?: string
    pageHead: PageHeadingTS
    brand: {
        id: number
        slug: string
        name: string
    }
}>()

const search = ref('')
const catalog = ref<CatalogProduct[]>([])
const nextPageUrl = ref<string | null>(null)
const isLoading = ref(true)
const isLoadingMore = ref(false)
const picked = ref<Record<string, PickedProduct>>({})
const expandedOptions = ref<Record<string, boolean>>({})

const optionKey = (productId: number, variationId: number | null) => `${productId}-${variationId ?? 0}`

const pickedList = computed(() => Object.values(picked.value))

const fetchCatalog = async (url?: string) => {
    if (url) {
        isLoadingMore.value = true
    } else {
        isLoading.value = true
    }

    try {
        const searchParams = search.value ? { 'filter[global]': search.value } : {}
        const response = await axios.get(url || route('kasir.brands.products.catalog', { brand: props.brand.id, ...searchParams }))

        catalog.value = url ? [...catalog.value, ...response.data.data] : response.data.data
        nextPageUrl.value = response.data.links?.next ?? null
    } catch (error) {
        notify({
            title: 'Oops! Ada kesalahan',
            text : 'Gagal mengambil produk katalog. Silahkan coba lagi.',
            type : 'error',
        })
    }

    isLoading.value = false
    isLoadingMore.value = false
}

const onSearch = debounce(() => fetchCatalog(), 400)

const isPicked = (productId: number, variationId: number | null) => optionKey(productId, variationId) in picked.value

const isExpanded = (productId: number, variationId: number | null) => !!expandedOptions.value[optionKey(productId, variationId)]

const toggleExpand = (productId: number, variationId: number | null) => {
    const key = optionKey(productId, variationId)

    expandedOptions.value[key] = !expandedOptions.value[key]
}

const pickedCountFor = (product: CatalogProduct) => pickedList.value
    .filter(pick => pick.cs_product_id === product.id).length

const variationSuffix = (product: CatalogProduct) => product.variations.length
    ? `+${product.variations.length} varian`
    : undefined

const pickedSummary = (product: CatalogProduct) => pickedCountFor(product)
    ? `${pickedCountFor(product)} dipilih`
    : undefined

const setPicked = (product: CatalogProduct, variation: CatalogVariation | undefined, shouldPick: boolean) => {
    const key = optionKey(product.id, variation?.id ?? null)

    if (!shouldPick) {
        delete picked.value[key]

        return
    }

    if (key in picked.value) {
        return
    }

    const catalogTiers = variation?.price_tiers ?? product.price_tiers

    picked.value[key] = {
        cs_product_id           : product.id,
        cs_product_variation_id : variation?.id ?? null,
        label                   : variation ? `${product.name} - ${variation.name}` : product.name,
        catalog_tiers           : catalogTiers,
        tiers                   : catalogTiers.map(tier => ({ ...tier })),
    }
}

/**
 * Picking a product picks its variations along with it, unpicking releases them again.
 * Single variations stay togglable on their own afterwards.
 */
const toggleProduct = (product: CatalogProduct) => {
    const shouldPick = !isPicked(product.id, null)

    setPicked(product, undefined, shouldPick)
    product.variations.forEach(variation => setPicked(product, variation, shouldPick))
}

const toggleVariation = (product: CatalogProduct, variation: CatalogVariation) =>
    setPicked(product, variation, !isPicked(product.id, variation.id))

const resetPickedTiers = (productId: number, variationId: number | null) => {
    const pick = picked.value[optionKey(productId, variationId)]

    if (pick) {
        pick.tiers = pick.catalog_tiers.map(tier => ({ ...tier }))
    }
}

const pickedTiers = (productId: number, variationId: number | null) => picked.value[optionKey(productId, variationId)]?.tiers

const isAllVariationsPicked = (product: CatalogProduct) => product.variations.length > 0
    && product.variations.every(variation => isPicked(product.id, variation.id))

const toggleAllVariations = (product: CatalogProduct) => {
    const shouldPick = !isAllVariationsPicked(product)

    product.variations.forEach(variation => setPicked(product, variation, shouldPick))
}

const isTiersChanged = (pick: PickedProduct) => JSON.stringify(pick.tiers) !== JSON.stringify(pick.catalog_tiers)

const cleanTiers = (tiers: PriceTier[]) => tiers
    .filter(tier => Number.isFinite(Number(tier.price)))
    .map(tier => ({ min_quantity: Number(tier.min_quantity) || 0, price: Number(tier.price) }))

const submitForm = useForm({
    products: [] as { cs_product_id: number, cs_product_variation_id: number | null, prices: PriceTier[] }[],
})

const isSubmitting = ref(false)

const submit = () => {
    submitForm.products = pickedList.value.map(pick => ({
        cs_product_id           : pick.cs_product_id,
        cs_product_variation_id : pick.cs_product_variation_id,
        prices                  : isTiersChanged(pick) ? cleanTiers(pick.tiers) : [],
    }))

    isSubmitting.value = true

    submitForm.post(route('kasir.brands.products.store', props.brand.id), {
        onSuccess: () => picked.value = {},
        onFinish : () => isSubmitting.value = false,
    })
}

const cancel = () => router.visit(route('kasir.brands.show', props.brand.slug))

let stopLeaveGuard: (() => void) | null = null

onMounted(() => {
    fetchCatalog()

    stopLeaveGuard = router.on('before', (event) => {
        if (!pickedList.value.length || isSubmitting.value) {
            return
        }

        if (!confirm('Produk yang dipilih belum disimpan. Tetap tinggalkan halaman ini?')) {
            event.preventDefault()
        }
    })
})

onUnmounted(() => stopLeaveGuard?.())
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="mx-auto flex max-w-3xl flex-col gap-3 px-3 py-4 sm:px-4">
        <div class="text-xs text-gray-500">
            Mencentang produk otomatis ikut mencentang semua variannya, dan varian bisa dilepas satu per satu. Harga mengikuti produk kasir, bisa diatur per qty sebelum disimpan.
        </div>

        <div class="flex items-start gap-x-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
            <FontAwesomeIcon icon="fal fa-info-circle" class="mt-0.5 shrink-0" fixed-width aria-hidden="true" />
            <span>
                <span class="font-semibold">Produk umum</span> tidak ditampilkan di daftar ini, karena sudah otomatis tersedia untuk semua brand
                (langsung bisa dipilih di Pesanan dan diambil dari stok umum).
                <Link :href="route('kasir.stocks.index')" class="font-medium underline">Lihat stok produk umum</Link>
                &middot;
                <a :href="route('guide.public', 'produk-umum')" target="_blank" rel="noopener" class="font-medium underline">Panduan produk umum</a>
            </span>
        </div>

        <div class="sticky top-0 z-10 -mx-3 bg-gray-50 px-3 py-2 sm:-mx-4 sm:px-4">
            <div class="relative">
                <FontAwesomeIcon icon="fal fa-search" class="pointer-events-none absolute inset-y-0 left-3 my-auto h-4 text-gray-400" aria-hidden="true" />
                <input
                    v-model="search"
                    type="search"
                    placeholder="Cari nama, SKU, atau kategori"
                    class="w-full rounded-md border-gray-300 pl-9 text-sm"
                    @input="onSearch"
                />
            </div>
        </div>

        <div class="rounded-xl border border-gray-200 bg-white px-2 py-1">
            <div v-if="isLoading" class="flex flex-col gap-2 py-2">
                <div v-for="skeleton in 5" :key="skeleton" class="animate-pulse rounded-lg bg-gray-100 p-3">
                    <div class="h-3 w-2/5 rounded bg-gray-200" />
                    <div class="mt-2 h-3 w-1/4 rounded bg-gray-200" />
                </div>
            </div>

            <div v-else-if="!catalog.length" class="flex flex-col items-center gap-2 py-12 text-center text-sm text-gray-400">
                <FontAwesomeIcon icon="fal fa-box-open" class="text-2xl" aria-hidden="true" />
                <span v-if="search">Produk tidak ditemukan</span>
                <span v-else>Semua produk katalog sudah ada di brand ini</span>
            </div>

            <div v-else class="divide-y divide-gray-100">
                <div v-for="product in catalog" :key="product.id" class="py-2">
                    <CatalogOptionRow
                        v-if="product.is_available"
                        :title="product.name"
                        :titleSuffix="variationSuffix(product)"
                        :subtitle="product.sku"
                        :meta="`${product.category || 'Tanpa kategori'} · ${product.print_type_label}`"
                        :summary="pickedSummary(product)"
                        :catalogTiers="product.price_tiers"
                        :tiers="pickedTiers(product.id, null)"
                        :picked="isPicked(product.id, null)"
                        :expanded="isExpanded(product.id, null)"
                        :expandable="product.variations.length > 0 || isPicked(product.id, null)"
                        @toggle="toggleProduct(product)"
                        @reset="resetPickedTiers(product.id, null)"
                        @toggleExpand="toggleExpand(product.id, null)"
                    />

                    <div v-else class="flex items-start">
                        <button
                            v-if="product.variations.length"
                            type="button"
                            class="mt-1 shrink-0 px-2 py-2 text-gray-400 hover:text-gray-600"
                            :aria-expanded="isExpanded(product.id, null)"
                            v-tooltip="isExpanded(product.id, null) ? 'Tutup varian' : 'Lihat varian yang belum dipasang'"
                            @click="toggleExpand(product.id, null)"
                        >
                            <FontAwesomeIcon :icon="isExpanded(product.id, null) ? 'fal fa-chevron-down' : 'fal fa-chevron-right'" fixed-width aria-hidden="true" />
                        </button>
                        <span v-else class="mt-1 shrink-0 px-2 py-2" aria-hidden="true">
                            <span class="inline-block w-[1.25em]" />
                        </span>

                        <div class="min-w-0 flex-1 py-2">
                            <div class="text-sm font-medium text-gray-500">
                                {{ product.name }}
                                <span v-if="variationSuffix(product)" class="text-xs font-normal text-gray-400">({{ variationSuffix(product) }})</span>
                            </div>
                            <div class="font-mono text-xs text-gray-400">{{ product.sku }} · sudah jadi produk brand</div>
                        </div>
                    </div>

                    <div v-if="isExpanded(product.id, null) && product.variations.length" class="ml-6 mt-1 border-l border-gray-200 pl-2">
                        <div class="flex items-center justify-between px-2 py-1">
                            <span class="text-xs uppercase tracking-wide text-gray-400">Varian</span>
                            <button type="button" class="text-xs text-indigo-600 hover:underline" @click="toggleAllVariations(product)">
                                {{ isAllVariationsPicked(product) ? 'Batal pilih semua' : 'Pilih semua varian' }}
                            </button>
                        </div>

                        <CatalogOptionRow
                            v-for="variation in product.variations"
                            :key="variation.id"
                            :title="variation.name"
                            :meta="variation.detail"
                            :catalogTiers="variation.price_tiers"
                            :tiers="pickedTiers(product.id, variation.id)"
                            :picked="isPicked(product.id, variation.id)"
                            :expanded="isExpanded(product.id, variation.id)"
                            :expandable="isPicked(product.id, variation.id)"
                            @toggle="toggleVariation(product, variation)"
                            @reset="resetPickedTiers(product.id, variation.id)"
                            @toggleExpand="toggleExpand(product.id, variation.id)"
                        />
                    </div>
                </div>

                <div v-if="nextPageUrl" class="py-3 text-center">
                    <Button
                        size="xs"
                        :style="'tertiary'"
                        label="Muat produk lainnya"
                        :loading="isLoadingMore"
                        @click="fetchCatalog(nextPageUrl)"
                    />
                </div>
            </div>
        </div>

        <div v-if="submitForm.errors.products" class="text-xs text-red-500">{{ submitForm.errors.products }}</div>

        <div class="sticky bottom-0 -mx-3 flex items-center gap-2 border-t border-gray-200 bg-white px-3 py-3 sm:-mx-4 sm:px-4">
            <div class="flex-1 text-xs text-gray-500">{{ pickedList.length }} dipilih</div>
            <Button :style="'tertiary'" label="Batal" @click="cancel" />
            <Button
                :style="'save'"
                icon="fal fa-plus"
                :label="`Tambahkan${pickedList.length ? ` (${pickedList.length})` : ''}`"
                :disabled="!pickedList.length"
                :loading="submitForm.processing"
                @click="submit"
            />
        </div>
    </div>
</template>
